import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SubmitButton } from './SubmitButton';
import { confirmPasswordReset } from './api';
import { useFormSubmit } from './useFormSubmit';
import { usePasswordPair } from './usePasswordPair';

interface Props {
  email: string;
  onBack: () => void;
}

/**
 * Step 2 of the forgot-password flow: the code sent by ForgotPasswordForm, plus a new
 * password. Cognito's ConfirmForgotPassword does not return tokens, so on success this
 * shows a confirmation message rather than redirecting — the user signs in normally
 * afterwards with their new password.
 */
export const ResetPasswordForm = ({ email, onBack }: Props) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [success, setSuccess] = useState(false);
  const { loading, error, setError, runSubmit } = useFormSubmit();
  const { newPassword, mismatch, fields, isIncomplete } = usePasswordPair({
    newPassword: t('login.resetPassword.newPasswordLabel'),
    confirm: t('login.resetPassword.confirmLabel'),
    mismatch: t('login.resetPassword.mismatch'),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mismatch) return;
    await runSubmit(async () => {
      await confirmPasswordReset({ email, code, newPassword });
      setSuccess(true);
    });
  };

  if (success) {
    return (
      <Stack spacing={2.5}>
        <Alert severity="success">{t('login.resetPassword.success')}</Alert>
        <Button variant="contained" size="large" fullWidth onClick={onBack} sx={{ height: '3rem' }}>
          {t('action.backToLogin')}
        </Button>
      </Stack>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={2.5}>
        <Typography variant="body2" color="text.secondary">
          {t('login.resetPassword.description')}
        </Typography>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          id="reset-password-code"
          label={t('login.resetPassword.codeLabel')}
          value={code}
          onChange={(e) => setCode(e.target.value.trim())}
          required
          autoComplete="one-time-code"
          autoFocus
          fullWidth
          size="small"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', maxLength: 8 }}
        />

        {fields}

        <SubmitButton loading={loading} disabled={!code || isIncomplete}>
          {t('login.resetPassword.submit')}
        </SubmitButton>

        <Button variant="text" size="small" onClick={onBack} sx={{ alignSelf: 'center' }}>
          {t('action.backToLogin')}
        </Button>
      </Stack>
    </Box>
  );
};
